// src/shared/components/Layout/SideBar.tsx
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/auth';
import { MENU } from '@/shared/navigation/menu';
import TenantSelector from './TenantSelector';
import CreditBadge from '@/shared/components/telemetry/CreditBadge';

const ALLOWED_PATHS = new Set(MENU.map(m => m.path));

type MenuItem = { path: string; label: string; ability?: string };

const BASE_LINK   = 'block px-4 py-2 rounded-md border border-transparent text-neon_purple-300 hover:text-neon_purple-100 hover:border-neon_purple-700 transition-colors';
const ACTIVE_LINK = 'pac-glass text-neon_orange-300 font-bold border-neon_orange-500';
const ROOT        = 'relative h-screen w-52 bg-black border-r border-neon_purple-900/40 text-neon_purple-300 flex flex-col';
const LOGOUT_BTN  = 'pac-glass-btn absolute bottom-4 left-4 right-4 text-neon_red-400 hover:border-neon_red-500 px-4 py-2 rounded text-left';

export default function Sidebar() {
  const navigate = useNavigate();
  const { nav = [], loading, logout, capabilities } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);

  return (
    <aside className={ROOT}>
      <div className="p-4 text-lg font-semibold flex items-center justify-between text-neon_purple-100">
        <span>PACMaster</span>
        <CreditBadge showPct />
      </div>

      {/* ── Tenant switcher (superadmin only) ── */}
      <TenantSelector />

      <nav className="flex-1 space-y-1 overflow-y-auto">
        {loading && (
          <div className="px-4 py-2 text-neon_purple-500">Loading menu…</div>
        )}
        {!loading && nav.length === 0 && (
          <div className="px-4 py-2 text-neon_purple-500">No sections available.</div>
        )}
        {nav
          .filter(({ path }: MenuItem) => {
            // Whitelist: only show items present in the static MENU
            if (!ALLOWED_PATHS.has(path)) return false;
            // Additional special-case: the base TT-Lock dashboard root requires admin capability
            if (path === '/ttlock' && !capabilities?.canAdminTtlockRooms) return false;
            return true;
          })
          .map(({ path, label }: MenuItem) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) => `${BASE_LINK} ${isActive ? ACTIVE_LINK : ''}`}
            end
            onMouseDown={(e) => {
              if ((e as any)?.button !== 1) return;
              e.preventDefault();
              try {
                const abs = new URL(String(path || ''), window.location.origin).toString();
                window.open(abs, '_blank', 'noopener,noreferrer');
              } catch {
                try {
                  const href = String(path || '');
                  if (href) window.open(href, '_blank', 'noopener,noreferrer');
                } catch {
                  // ignore
                }
              }
            }}
            onAuxClick={(e) => {
              // Middle mouse button should open in a new browser tab.
              if ((e as any)?.button !== 1) return;
              e.preventDefault();
              try {
                const href = String(path || '');
                if (href) window.open(href, '_blank', 'noopener,noreferrer');
              } catch {
                // ignore
              }
            }}
          >
            {label}
          </NavLink>
        ))}
      </nav>

      <button
        onClick={async () => {
          setLoggingOut(true);
          try { await logout(); } finally {
            setLoggingOut(false);
            navigate('/login', { replace: true });
          }
        }}
        disabled={loggingOut}
        className={`${LOGOUT_BTN} ${loggingOut ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        {loggingOut ? 'Logging out…' : 'Log out'}
      </button>
    </aside>
  );
}
